import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  ProjectArchive,
  ProjectPhase,
  ProjectStatus,
  PrepareResult,
  ContractOutput,
  BattleResult,
  MaintenanceResult,
  SettlementResult,
} from '../types/phase-contracts';

import { mapContractOutputToContractResult } from '../formulas/contract-mapping';
import { usePlayerStore } from './playerStore';

interface ProjectStore {
  projects: Record<string, ProjectArchive>;
  activeProjectId: string | null;

  createProject: (prepareSnapshot: PrepareResult) => string;
  getProject: (projectId: string) => ProjectArchive | undefined;
  setActiveProject: (projectId: string | null) => void;
  completeContract: (projectId: string, output: ContractOutput, projectName: string) => void;
  completeBattle: (projectId: string, result: BattleResult) => void;
  completeMaintenance: (projectId: string, result: MaintenanceResult) => void;
  completeSettlement: (projectId: string, result: SettlementResult) => void;
  getProjectsForPhase: (phase: ProjectPhase) => ProjectArchive[];
  hasUnplayedProjects: (phase: ProjectPhase) => boolean;
  abandonProject: (projectId: string) => void;
}

function generateProjectId(): string {
  return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
}

export const useProjectStore = create<ProjectStore>()(
  persist(
    (set, get) => ({
      projects: {},
      activeProjectId: null,

      createProject: (prepareSnapshot) => {
        const projectId = generateProjectId();
        const now = Date.now();
        const project: ProjectArchive = {
          projectId,
          projectName: '',
          createdAt: now,
          updatedAt: now,
          status: 'active',
          prepareSnapshot: structuredClone(prepareSnapshot),
          contractOutput: null,
          contractResult: null,
          contractCompletedAt: null,
          battleResult: null,
          battleCompletedAt: null,
          maintenanceResult: null,
          maintenanceCompletedAt: null,
          settlementResult: null,
        };
        set((s) => ({
          projects: { ...s.projects, [projectId]: project },
          activeProjectId: projectId,
        }));
        return projectId;
      },

      getProject: (projectId) => get().projects[projectId],

      setActiveProject: (projectId) => set({ activeProjectId: projectId }),

      completeContract: (projectId, output, projectName) => {
        const contractResult = mapContractOutputToContractResult(output, projectId, projectName);

        set((s) => {
          const project = s.projects[projectId];
          if (!project) return s;

          return {
            projects: {
              ...s.projects,
              [projectId]: {
                ...project,
                projectName,
                contractOutput: output,
                contractResult,
                contractCompletedAt: Date.now(),
                updatedAt: Date.now(),
              },
            },
          };
        });

        const deposit = contractResult.project.deposit;
        if (deposit > 0) {
          usePlayerStore.getState().addFlop(deposit);
        }
      },

      completeBattle: (projectId, result) =>
        set((s) => {
          const project = s.projects[projectId];
          if (!project) return s;
          return {
            projects: {
              ...s.projects,
              [projectId]: {
                ...project,
                battleResult: result,
                battleCompletedAt: Date.now(),
                updatedAt: Date.now(),
              },
            },
          };
        }),

      completeMaintenance: (projectId, result) =>
        set((s) => {
          const project = s.projects[projectId];
          if (!project) return s;
          return {
            projects: {
              ...s.projects,
              [projectId]: {
                ...project,
                maintenanceResult: result,
                maintenanceCompletedAt: Date.now(),
                status: 'completed' as ProjectStatus,
                updatedAt: Date.now(),
              },
            },
          };
        }),

      completeSettlement: (projectId, result) =>
        set((s) => {
          const project = s.projects[projectId];
          if (!project) return s;
          return {
            projects: {
              ...s.projects,
              [projectId]: {
                ...project,
                settlementResult: result,
                status: 'completed' as ProjectStatus,
                updatedAt: Date.now(),
              },
            },
          };
        }),

      getProjectsForPhase: (phase) => {
        const { projects } = get();
        const active = Object.values(projects).filter((p) => p.status === 'active');
        switch (phase) {
          case 'contract':
            return active;
          case 'battle':
            return active.filter((p) => p.contractOutput !== null);
          case 'maintenance':
            return active.filter((p) => p.battleResult !== null);
        }
      },

      hasUnplayedProjects: (phase) => {
        const { projects } = get();
        const active = Object.values(projects).filter((p) => p.status === 'active');
        switch (phase) {
          case 'contract':
            return active.some((p) => p.contractOutput === null);
          case 'battle':
            return active.some((p) => p.contractOutput !== null && p.battleResult === null);
          case 'maintenance':
            return active.some((p) => p.battleResult !== null && p.maintenanceResult === null);
        }
      },

      abandonProject: (projectId) =>
        set((s) => {
          const project = s.projects[projectId];
          if (!project) return s;
          return {
            projects: {
              ...s.projects,
              [projectId]: {
                ...project,
                status: 'abandoned' as ProjectStatus,
                updatedAt: Date.now(),
              },
            },
            activeProjectId: s.activeProjectId === projectId ? null : s.activeProjectId,
          };
        }),
    }),
    { name: 'silicon-valley-projects' },
  ),
);
